// src/load/GoogleMaps.js
let SCRIPT_ID = "googleMaps";
let loading = false;
let loaded = false;
let callbacks = [];

function buildSrcFromMapKey() {
  const mk = window.mapKey; // may be a full URL or just the API key string
  const libs = "places,geometry";

  // If mapKey is a full URL, use as-is (but normalize the host if it's the old domain)
  if (typeof mk === "string" && /^https?:\/\//i.test(mk)) {
    // Ensure it points to maps.googleapis.com
    return mk.replace(
      /https:\/\/maps\.google\.com\/maps\/api\/js/i,
      "https://maps.googleapis.com/maps/api/js"
    );
  }

  // Otherwise, treat mapKey as the API key
  const key = mk || "";
  const base = "https://maps.googleapis.com/maps/api/js";
  const params = new URLSearchParams({
    key,
    libraries: libs,
    // v: "weekly", // optional: uncomment to pin track
  });
  return `${base}?${params.toString()}`;
}

function flush() {
  const cbs = callbacks.slice();
  callbacks = [];
  cbs.forEach((cb) => {
    try {
      cb && cb();
    } catch (e) {
      // no-op
    }
  });
}

/**
 * Load Google Maps JS once, queueing callbacks until ready.
 * Usage: loadGoogleMaps(() => { /* google is ready *\/ });
 */
export const loadGoogleMaps = (callback) => {
  if (typeof callback === "function") callbacks.push(callback);

  // If already loaded, fire immediately
  if (loaded && window.google && window.google.maps) {
    flush();
    return;
  }

  // If a script tag exists:
  const existingScript = document.getElementById(SCRIPT_ID);

  // If script already on page but not yet loaded, attach onload/onerror and wait
  if (existingScript && !loaded) {
    if (!loading) {
      // We don't know its listeners; add ours
      existingScript.addEventListener("load", onload, { once: true });
      existingScript.addEventListener("error", onerror, { once: true });
      loading = true; // we now track it
    }
    return;
  }

  // If script already loaded (e.g., another part of app), fire callbacks
  if (existingScript && window.google && window.google.maps) {
    loaded = true;
    flush();
    return;
  }

  // Not present: create and load
  const script = document.createElement("script");
  script.id = SCRIPT_ID;
  script.src = buildSrcFromMapKey();
  script.async = true;
  script.defer = true;
  script.onload = onload;
  script.onerror = onerror;

  loading = true;
  document.head.appendChild(script);
};

function onload() {
  loaded = true;
  loading = false;
  flush();
}

function onerror() {
  // don't leave callers hanging
  loading = false;
  flush();
}

/**
 * Remove Google Maps script from the page (optional).
 * Only call this if you truly want to unload the SDK between views.
 */
export const removeGoogleMaps = () => {
  const s = document.getElementById(SCRIPT_ID);
  if (s && s.parentNode) s.parentNode.removeChild(s);

  // Best effort cleanup flags
  loaded = false;
  loading = false;
  callbacks = [];

  // Optionally clear the global (not strictly necessary)
  try {
    delete window.google;
  } catch {
    window.google = undefined;
  }
};

export const mapKey = window.mapKey; // keep your export if other code imports it